import { memo, ReactElement, useCallback } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import { Dispatch, UnknownAction } from "redux";

//UI COMPONENTS
import Button from "../../../../../../components/Button/Button";
import InstrumentLimits from "../../../InstrumentLimits/InstrumentLimits";

//TYPES
import { SortDescriptor } from "@nextui-org/react";
import { Instrument as InstrumentType, Quote } from "../../../../types";
import { AccordionTableColumn, StocksColumn, StocksRow } from "../../types";
import {
  Instrument,
  LastPrice,
  SharePrice,
  SharePriceDetail,
  Starred,
  Variation,
  Volume,
  VolumeAmount,
} from "../Cedears";

// UTILS
import { PATH_APP } from "../../../../../../routes/paths";
import { ALLARIA_LENDAR, createPriceClickHandler } from "../../utils";

//STYLES
import { useAppDispatch, useAppSelector } from "../../../../../../store/hooks";
import {
  openOperationModal,
  updateOperationData,
} from "../../../../../../store/slices/operationDataSlice";
import styles from "./Stocks.module.scss";
import { useOperationTime } from "../../../../../../hooks/useOperationTime";
import useFeatures from "../../../../../../hooks/useFeatures";

export const stockColumns: AccordionTableColumn<StocksColumn>[] = [
  {
    key: "instrument",
    label: "Instrumento",
    align: "start",
    sortable: true,
  },
  {
    key: "lastPrice",
    label: "Último precio",
    align: "end",
    sortable: true,
  },
  {
    key: "variation",
    label: "Variación",
    align: "end",
    sortable: true,
  },
  {
    key: "sell",
    label: "Vender",
    align: "end",
  },
  {
    key: "buy",
    label: "Comprar",
    align: "end",
  },
  {
    key: "volume",
    label: "Volumen",
    align: "end",
    sortable: true,
  },
  {
    key: "volumeAmount",
    label: "Volumen monto",
    align: "end",
    sortable: true,
  },
  {
    key: "vwap",
    label: "VWAP",
    align: "end",
    sortable: true,
  },
  {
    key: "starred",
    label: "",
    align: "center",
  },
];
const CURRENCY_LABEL_MAP: Record<string, string> = {
  DOLLAR_CCL: "CABLE",
  DOLLAR_MEP: "MEP",
  ARS: "ARS",
};
const CURRENCY_MAP: Record<string, string> = {
  DOLLAR_CCL: "DOLLAR_CCL",
  DOLLAR_MEP: "DOLLAR_MEP",
  "USD-M": "DOLLAR_MEP",
  "USD-CCL": "DOLLAR_CCL",
  ARS: "ARS",
};

const getCurrencyLabel = (currency?: string): string => {
  return currency ? CURRENCY_LABEL_MAP[currency] || currency : "";
};

export const navigateToInstrumentDetail = ({
  symbol,
  instrument,
  navigate,
  settledPeriod,
  currency,
  fundId,
}: Partial<Pick<Quote, "settledPeriod" | "currency">> & {
  symbol: string;
  navigate: ReturnType<typeof useNavigate>;
  instrument?: InstrumentType | "FUNDS";
  fundId?: string;
}) => {
  const isFund = instrument === "FUNDS";
  const currencyLabel = getCurrencyLabel(currency);

  const searchParams: Record<string, string> = {
    symbol: isFund && currencyLabel ? `${symbol} - ${currencyLabel}` : symbol,
  };

  if (instrument) searchParams.instrument = instrument;
  if (settledPeriod) searchParams.settledPeriod = settledPeriod;
  if (currency && CURRENCY_MAP[currency])
    searchParams.currency = CURRENCY_MAP[currency];
  if (isFund && fundId) searchParams.fund_id = fundId;

  navigate({
    pathname: PATH_APP.instrument,
    search: createSearchParams(searchParams).toString(),
  });
};

// Memoized component to prevent unnecessary re-renders
export const InstrumentDetail = memo(
  ({
    openingPrice,
    closingPrice,
    highestDailyTrade,
    lowestDailyTrade,
    icon,
  }: {
    openingPrice: number;
    closingPrice: number;
    highestDailyTrade: number;
    lowestDailyTrade: number;
    icon?: ReactElement;
  }) => (
    <div className="flex gap-4 absolute w-[24rem] items-center">
      {!!icon && <div className={styles.logo}>{icon}</div>}
      <InstrumentLimits
        max={highestDailyTrade}
        min={lowestDailyTrade}
        opening={openingPrice}
        closing={closingPrice}
      />
    </div>
  )
);
InstrumentDetail.displayName = "InstrumentDetail";

export const InstrumentActions = memo(
  ({
    symbol,
    type = "STOCKS",
    settledPeriod,
    currency,
    fundId,
    nonOperable,
  }: Partial<Omit<Quote, "type">> & {
    type: InstrumentType | "FUNDS";
    fundId?: string;
    nonOperable?: boolean;
  }) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { isEnabledOperation } = useOperationTime({ symbolType: type });

    const { isOperateEnabled: isOperateGloballyEnabled } = useFeatures();

    const { accountSelected } = useAppSelector((state) => state.userAccounts);
    const number = accountSelected?.number;

    const { settlement } = useAppSelector((state) => state.market.filters);

    const handleViewDetail = useCallback(() => {
      if (symbol) {
        navigateToInstrumentDetail({
          navigate,
          symbol,
          instrument: type,
          settledPeriod,
          currency,
          fundId,
        });
      }
    }, [symbol, type]);

    return (
      <div className="flex flex-col gap-2 absolute right-4 mt-7">
        <Button
          variant="secondary"
          text="Ver detalle"
          onPress={symbol ? handleViewDetail : undefined}
        />
        {symbol !== ALLARIA_LENDAR && (
          <Button
            isDisabled={
              (type === "FUNDS" ? false : !accountSelected?.isEnabledOperate) ||
              !isEnabledOperation ||
              !isOperateGloballyEnabled
            }
            text="Operar"
            onPress={() => {
              const hasLiquidation = ![
                "FUNDS",
                "REPO_OPERATION",
                "OPTIONS",
              ].includes(type);

              //Funds Operations Format
              const isFundOperation = type === "FUNDS";
              const [fund, fundClass, currency] = symbol?.split("-") || [];

              const operationData = {
                account: number,
                symbol: isFundOperation ? fund.trim() : symbol,
                symbolType: type,
                orderType: "limited",
                side: nonOperable ? "SELL" : "BUY",
                nonOperable: nonOperable ?? false,
                ...(hasLiquidation ? { liquidation: settlement } : {}),
                ...(isFundOperation ? { fundClass: fundClass.trim() } : {}),
                ...(isFundOperation
                  ? { currency: currency ? "DOLLAR_MEP" : "ARS" }
                  : {}),
              };

              dispatch(updateOperationData(operationData));
              dispatch(openOperationModal());
            }}
          />
        )}
      </div>
    );
  }
);
InstrumentActions.displayName = "InstrumentActions";

export const getAccordionStockCellValue = ({
  column,
  row: {
    quote: {
      key,
      symbol,
      lastTrade,
      lastTradeUpdatedAt,
      currency,
      percentChange,
      volumeAmount,
      bids,
      offers,
      nominalVariation,
      weightedAveragePrice,
      volume,
      type,
    },
    staticQuote: { company, icon },
    isStarred,
    onStarClicked,
  },
  accountNumber,
  dispatch,
  isEnabledOperation,
  canSellMap = {},
  settlement,
}: {
  column: StocksColumn;
  row: StocksRow;
  accountNumber: string | undefined;
  dispatch: Dispatch<UnknownAction>;
  isEnabledOperation: boolean;
  canSellMap?: Record<string, boolean>;
  settlement?: string;
}) => {
  switch (column) {
    case "instrument":
      return (
        <Instrument
          description={company}
          icon={icon}
          symbol={symbol}
          key={key}
        />
      );
    case "lastPrice":
      return (
        <LastPrice
          value={lastTrade}
          currency={currency}
          updatedAt={lastTradeUpdatedAt}
        />
      );
    case "variation":
      return <Variation percent={percentChange} value={nominalVariation} />;
    case "sell":
      return (
        <SharePrice
          data={bids}
          onClick={createPriceClickHandler(
            accountNumber,
            symbol,
            type,
            "SELL",
            dispatch,
            settlement,
          )}
          isEnabledOperation={isEnabledOperation}
          canSell={canSellMap[symbol]}
        />
      );
    case "buy":
      return (
        <SharePrice
          buy
          data={offers}
          onClick={createPriceClickHandler(
            accountNumber,
            symbol,
            type,
            "BUY",
            dispatch,
            settlement,
          )}
          isEnabledOperation={isEnabledOperation}
        />
      );
    case "volume":
      return <Volume value={volume} />;
    case "volumeAmount":
      return <VolumeAmount value={volumeAmount} currency={currency} />;
    case "vwap":
      return <VolumeAmount value={weightedAveragePrice} currency={currency} />;
    case "starred":
      return <Starred isStarred={isStarred} onStarClicked={onStarClicked} />;
    default:
      return "";
  }
};

export const getStockCellValue = ({
  column,
  row: {
    quote,
    staticQuote: { icon },
  },
  accountNumber,
  dispatch,
  isEnabledOperation,
  canSellMap = {},
  settlement,
}: {
  column: StocksColumn;
  row: StocksRow;
  accountNumber: string | undefined;
  dispatch: Dispatch<UnknownAction>;
  isEnabledOperation: boolean;
  canSellMap?: Record<string, boolean>;
  settlement?: string;
}) => {
  const {
    symbol,
    openingPrice,
    closingPrice,
    highestDailyTrade,
    lowestDailyTrade,
    bids,
    offers,
    type,
  } = quote;

  switch (column) {
    case "instrument":
      return (
        <InstrumentDetail
          openingPrice={openingPrice}
          closingPrice={closingPrice}
          highestDailyTrade={highestDailyTrade}
          lowestDailyTrade={lowestDailyTrade}
          icon={icon}
        />
      );
    case "sell":
      return (
        <SharePriceDetail
          data={bids}
          onClick={createPriceClickHandler(
            accountNumber,
            symbol,
            type,
            "SELL",
            dispatch,
            settlement
          )}
          isEnabledOperation={isEnabledOperation}
          canSell={canSellMap[symbol]}
        />
      );
    case "buy":
      return (
        <SharePriceDetail
          buy
          data={offers}
          onClick={createPriceClickHandler(
            accountNumber,
            symbol,
            type,
            "BUY",
            dispatch,
            settlement,
          )}
          isEnabledOperation={isEnabledOperation}
        />
      );
    case "starred":
      return (
        <InstrumentActions
          symbol={quote.symbol}
          type={quote.type}
          settledPeriod={quote.settledPeriod}
          currency={quote.currency}
        />
      );
    default:
      return <></>;
  }
};

const sortKeys: { [key in StocksColumn]: keyof Quote } = {
  instrument: "symbol",
  lastPrice: "lastTrade",
  variation: "percentChange",
  volume: "volume",
  sell: "symbol",
  buy: "symbol",
  starred: "symbol",
  volumeAmount: "volumeAmount",
  vwap: "weightedAveragePrice",
};

export const sortSotcksQuotes = (
  quote: Quote,
  other: Quote,
  by: SortDescriptor,
) => {
  const key = sortKeys[by.column as StocksColumn];
  const quoteValue = quote[key];
  const otherValue = other[key];

  // Handle null/undefined values
  if (quoteValue == null && otherValue == null) return 0;
  if (quoteValue == null) return 1;
  if (otherValue == null) return -1;

  let comparison = 0;
  if (quoteValue > otherValue) {
    comparison = 1;
  } else if (quoteValue < otherValue) {
    comparison = -1;
  }

  return by.direction === "descending" ? -comparison : comparison;
};
